import { mayLoadAnalytics, readConsentChoice } from '~/core/consent/choice';

type PostHogClient = typeof import('posthog-js').default;
type PostHogRequest = Parameters<PostHogClient['_send_request']>[0];

type EventConsent = { epoch: number; grantId: string };
const EVENT_CONSENT_TAG = '__directorDeckConsent';
type TaggedEvent = {
  event: string;
  [EVENT_CONSENT_TAG]?: EventConsent;
};

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || '/ingest';

let posthog: PostHogClient | null = null;
let initialization: Promise<boolean> | null = null;
let consentEpoch = 0;
let activeGrantId: string | null = null;
let identityResetPending = false;
const guardedSdks = new WeakSet<PostHogClient>();

function hasActiveAnalyticsGrant(): boolean {
  const choice = readConsentChoice();
  return (
    choice?.analytics === true &&
    !!activeGrantId &&
    choice.analyticsGrantId === activeGrantId
  );
}

function isCapturedEvent(data: unknown): data is TaggedEvent {
  return (
    !!data &&
    typeof data === 'object' &&
    'event' in data &&
    typeof data.event === 'string'
  );
}

function withoutConsentTag(event: TaggedEvent): TaggedEvent {
  const upload = { ...event };
  delete upload[EVENT_CONSENT_TAG];
  return upload;
}

function sendOnlyWithCurrentConsent(sdk: PostHogClient) {
  if (guardedSdks.has(sdk)) return;
  guardedSdks.add(sdk);
  const sendRequest = sdk._send_request.bind(sdk);
  sdk._send_request = (request: PostHogRequest) => {
    const choice = readConsentChoice();
    const hasCapture =
      isCapturedEvent(request.data) ||
      (Array.isArray(request.data) && request.data.some(isCapturedEvent));
    const isFlagsRequest = request.url.includes('/flags/');
    if (
      choice?.analytics !== true ||
      !choice.analyticsGrantId ||
      choice.analyticsGrantId !== activeGrantId
    ) {
      // The first /flags callback owns the SDK's remote config state. Reset
      // its in-flight marker without consuming that first response slot.
      if (isFlagsRequest) sdk.featureFlags.reset();
      else if (!hasCapture) request.callback?.({ statusCode: 0 });
      return;
    }

    // The SDK can flush a capture batch or retry after consent changes. Events
    // from an earlier choice must not be sent even if analytics is enabled again.
    const isCurrent = (event: TaggedEvent) => {
      const consent = event[EVENT_CONSENT_TAG];
      return (
        consent?.epoch === consentEpoch &&
        consent.grantId === choice.analyticsGrantId
      );
    };
    if (Array.isArray(request.data)) {
      const data = request.data
        .filter((event) => !isCapturedEvent(event) || isCurrent(event))
        .map((event) =>
          isCapturedEvent(event) ? withoutConsentTag(event) : event,
        );
      if (data.length === 0) return;
      request = { ...request, data };
    } else if (isCapturedEvent(request.data)) {
      if (!isCurrent(request.data)) return;
      request = { ...request, data: withoutConsentTag(request.data) };
    }

    const requestEpoch = consentEpoch;
    const requestGrantId = choice.analyticsGrantId;
    const callback = request.callback;
    sendRequest({
      ...request,
      callback: (response) => {
        const currentChoice = readConsentChoice();
        if (
          requestEpoch === consentEpoch &&
          currentChoice?.analytics === true &&
          currentChoice.analyticsGrantId === requestGrantId
        ) {
          callback?.(response);
        } else if (isFlagsRequest) {
          // A stale /flags response must not apply remote config or consume
          // the first valid response after a new grant.
          if (activeGrantId !== currentChoice?.analyticsGrantId)
            sdk.featureFlags.reset();
        } else if (!hasCapture) {
          callback?.({ statusCode: 0 });
        }
      },
    });
  };
}

function resetLoadedPostHog(sdk: PostHogClient) {
  try {
    sdk.reset(true);
    identityResetPending = false;
  } catch {
    // The pinned SDK's survey reset can throw when browser storage is blocked.
    // Do not start a new grant until a full reset can complete.
    identityResetPending = true;
  } finally {
    // reset() clears the SDK's opt-out before it reaches the throwing code.
    sdk.opt_out_capturing();
  }
}

export async function initPostHog(): Promise<boolean> {
  const choice = readConsentChoice();
  if (
    typeof window === 'undefined' ||
    choice?.analytics !== true ||
    !choice.analyticsGrantId
  )
    return false;
  if (process.env.NEXT_PUBLIC_EMULATOR === 'true' || !POSTHOG_KEY) return false;

  if (posthog) {
    if (activeGrantId && activeGrantId !== choice.analyticsGrantId)
      stopPostHog();
    if (identityResetPending) {
      resetLoadedPostHog(posthog);
      if (identityResetPending) return false;
    }
    activeGrantId = choice.analyticsGrantId;
    posthog.opt_in_capturing({ captureEventName: false });
    posthog.reloadFeatureFlags();
    return true;
  }
  if (initialization) return initialization;

  initialization = (async () => {
    const { default: sdk } = await import('posthog-js');
    // Consent can be withdrawn while the SDK chunk is downloading.
    const loadedChoice = readConsentChoice();
    if (loadedChoice?.analytics !== true || !loadedChoice.analyticsGrantId)
      return false;

    sendOnlyWithCurrentConsent(sdk);
    activeGrantId = loadedChoice.analyticsGrantId;
    try {
      sdk.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: 'history_change',
        capture_pageleave: true,
        autocapture: { capture_copied_text: true },
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: { password: true },
          maskTextSelector: '[data-ph-mask], [data-ph-mask] *',
          recordCrossOriginIframes: false,
        },
        capture_performance: true,
        respect_dnt: false,
        capture_exceptions: false,
        opt_out_persistence_by_default: true,
        advanced_disable_feature_flags: false,
        persistence: 'localStorage+cookie',
        before_send: (event) => {
          const choice = readConsentChoice();
          if (
            !event ||
            choice?.analytics !== true ||
            !choice.analyticsGrantId ||
            choice.analyticsGrantId !== activeGrantId
          )
            return null;
          // The SDK copies events before batching, so the tag travels with the
          // queued payload. sendOnlyWithCurrentConsent removes it before upload.
          (event as TaggedEvent)[EVENT_CONSENT_TAG] = {
            epoch: consentEpoch,
            grantId: choice.analyticsGrantId,
          };
          return event;
        },
      });
    } catch (error) {
      activeGrantId = null;
      throw error;
    }
    // A previous withdrawal persists an SDK opt-out across page loads.
    sdk.opt_in_capturing({ captureEventName: false });
    posthog = sdk;
    return true;
  })().finally(() => {
    initialization = null;
  });
  return initialization;
}

export function stopPostHog() {
  consentEpoch += 1;
  activeGrantId = null;
  if (!posthog) {
    clearStoredPostHogIdentity();
    return;
  }
  posthog.opt_out_capturing();
  posthog.stopSessionRecording();
  // opt-out disables storage but leaves its in-memory distinct ID and super
  // properties intact. Reset clears them before any later consent grant.
  resetLoadedPostHog(posthog);
}

function clearStoredPostHogIdentity() {
  if (typeof window === 'undefined' || !POSTHOG_KEY) return;
  const name = `ph_${POSTHOG_KEY}_posthog`;
  try {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  } catch {
    // Storage can be unavailable in privacy modes; the SDK was never loaded.
  }
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  const labels = location.hostname.split('.');
  for (let index = 0; index < labels.length - 1; index += 1) {
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${labels.slice(index).join('.')}; SameSite=Lax`;
  }
}

export function startSessionRecording() {
  if (hasActiveAnalyticsGrant()) posthog?.startSessionRecording();
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
) {
  if (hasActiveAnalyticsGrant()) posthog?.capture(event, properties);
}

export function identifyUser(
  userId: string,
  properties: {
    email?: string;
    name?: string;
    authProvider?: string;
    emailVerified?: boolean;
    mfaEnabled?: boolean;
    currentPlan?: string;
    totalPresentations?: number;
    totalVideosExported?: number;
    signupDate?: string;
  },
) {
  if (!mayLoadAnalytics()) return;
  posthog?.identify(userId, {
    email: properties.email,
    name: properties.name,
    auth_provider: properties.authProvider,
    email_verified: properties.emailVerified,
    mfa_enabled: properties.mfaEnabled,
    current_plan: properties.currentPlan,
    total_presentations: properties.totalPresentations,
    total_videos_exported: properties.totalVideosExported,
    signup_date: properties.signupDate,
  });
}

export function setOrganizationContext(orgId: string, orgName: string) {
  if (mayLoadAnalytics())
    posthog?.register({ organization_id: orgId, organization_name: orgName });
}

export function clearOrganizationContext() {
  posthog?.unregister('organization_id');
  posthog?.unregister('organization_name');
}

export function resetUser() {
  if (mayLoadAnalytics()) posthog?.reset();
}
